'use client';

import { storyblokEditable } from '@storyblok/react/rsc';
import { clsx } from 'clsx';
import { FC } from 'react';
import { AdventCalendarBlockStoryblok } from 'root/component-types-sb';
import { Button } from 'atoms/Button';
import { Container } from 'atoms/Container';
import { Image } from 'atoms/Image/Image';
import { Picture } from 'atoms/Picture';
import { P } from 'atoms/Typography';
import { Price } from 'molecules/Price';
import { RichTextMarkdown } from 'pages/StoryBlok/RichText';
import { useTranslations } from 'modules/i18n/useTranslations';
import { useRouter } from 'modules/next/hooks';
import styles from './styles.module.css';

export interface AdventCalendarBlockBodyProps
  extends AdventCalendarBlockStoryblok {
  price: { withTax: number; currencyCode: string };
  isSoldOut: boolean;
}

export const AdventCalendarBlockBody: FC<{
  blok: AdventCalendarBlockBodyProps;
}> = ({ blok }) => {
  const t = useTranslations();
  const { push } = useRouter();

  if (!blok) {
    return null;
  }

  const handleClick = () => {
    push(blok.href);
  };
  return (
    <Container
      {...storyblokEditable(blok)}
      className={clsx(
        styles.root,
        blok.right_side_content && styles.rightContent,
      )}
      style={{ backgroundColor: blok.background_color?.color }}
    >
      <Picture
        srcDesktop={blok.desktop_image}
        srcMobile={blok.mobile_image}
        rootClassName={styles.backgroundImage}
        className={styles.image}
      />
      <div
        className={clsx(
          styles.contentWrapper,
          blok.right_side_content && styles.right,
        )}
      >
        <div className={styles.info} style={{ color: blok.text_color?.color }}>
          <Image
            src={blok.title_image?.filename}
            alt={blok.title_image?.alt}
            className={styles.titleImage}
          />
          <RichTextMarkdown
            text={blok.features_list}
            className={styles.featuresList}
          />
          <P className={styles.featuresHeadline}>{blok.features_headline}</P>
          <Image
            src={blok.features_image?.filename}
            alt={blok.features_image?.alt}
            className={styles.featuresImage}
          />
          <div className={styles.priceBlock}>
            <Price
              amount={blok.price?.withTax}
              currencyCode={blok.price?.currencyCode}
              isCrossed={false}
              size="regular"
              className={styles.price}
            />
            <span className={styles.priceDisclaimer}>
              {t('advent-calendar.price-disclaimer')}
            </span>
          </div>
          <Button
            disabled={blok.isSoldOut}
            className={clsx(styles.button, styles.submitButton)}
            style={{ backgroundColor: blok.text_color?.color }}
            onClick={handleClick}
          >
            {blok.isSoldOut ? blok.button_sold_out : blok.button_add_to_cart}
          </Button>
        </div>
      </div>
    </Container>
  );
};
